import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subject, merge } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

/**
 * Says which fields are still holding a form back.
 *
 * <p>Forty-odd forms in this app disable their save button with `[disabled]="!form.valid"`. On a
 * short form that is fine, because the empty field is on screen. On a long one it is a dead end:
 * the button is grey, nothing says why, and a third of those forms have no error message anywhere
 * in the template, so there is literally nothing to read. Several also carry validators with no
 * message at all -- a payment term out of range, say -- which blocks the save with a blank screen.
 *
 * <p>This is additive on purpose. It does not enable the button or change what submits, so it
 * cannot let an invalid form through; it only tells the user what the button is waiting for.
 *
 * <p>Field names come from the template rather than the control names, because `contractAgreementNumber`
 * is not what the label next to the box says. It looks up the element bound to each invalid control
 * and reads the label above it, which is the text the user is actually looking for.
 */
@Component({
    selector: 'app-missing-fields',
    standalone: false,
    // Interpolation and a class binding rather than *ngIf, so this renders the same wherever it
    // is declared and does not depend on CommonModule reaching it.
    template: ` <span class="missing-fields" [class.form-error]="missing.length">{{ message }}</span> `,
    styles: [
        `
            .missing-fields {
                margin-right: 1.5rem;
                align-self: center;
            }
        `,
    ],
})
export class MissingFieldsComponent implements AfterViewInit, OnDestroy {
    @Input() form: AbstractControl;

    missing: string[] = [];

    get message(): string {
        return this.missing.length ? `Still needed: ${this.missing.join(', ')}` : '';
    }

    private readonly destroyed$ = new Subject<void>();

    constructor(
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly changeDetector: ChangeDetectorRef,
    ) {}

    ngAfterViewInit() {
        if (!this.form) {
            return;
        }
        merge(this.form.statusChanges, this.form.valueChanges)
            .pipe(startWith(null), takeUntil(this.destroyed$))
            .subscribe(() => this.recalculate());
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private recalculate() {
        const names = this.invalidControlNames(this.form as FormGroup, '');
        const scope = this.scope();
        // Deduplicated because two controls can sit under one label, and a label read twice reads
        // like two different things are wrong.
        this.missing = [...new Set(names.map((name) => this.labelFor(name, scope)).filter(Boolean))];
        // This runs from a form subscription rather than from an input, so the view is not dirty
        // and would keep rendering the previous list until something else happened to mark it.
        this.changeDetector.markForCheck();
    }

    /** Walks nested groups so `location.city` is found, not just the top level. */
    private invalidControlNames(group: FormGroup, prefix: string): string[] {
        if (!group || !group.controls) {
            return [];
        }
        return Object.keys(group.controls).flatMap((key) => {
            const control = group.controls[key];
            const path = prefix ? `${prefix}.${key}` : key;
            if (!control.invalid) {
                return [];
            }
            const nested = this.invalidControlNames(control as FormGroup, path);
            // A group is invalid only because a child is, so name the children, not the group.
            return nested.length ? nested : [path];
        });
    }

    /**
     * The footer holding this component is sometimes inside the form element and sometimes a
     * sibling of it, so look for the form first and fall back to the surrounding modal.
     */
    private scope(): ParentNode {
        const host = this.elementRef.nativeElement;
        return host.closest('form') ?? host.closest('.modal-wrapper, .modal-content, ngb-modal-window') ?? document;
    }

    private labelFor(path: string, scope: ParentNode): string {
        const name = path.split('.').pop();
        const field = scope.querySelector(`[formControlName="${name}"], [formcontrolname="${name}"]`);
        if (!field) {
            return '';
        }
        const label = field.closest('.form-group, .form-group--toggle')?.querySelector('label');
        return (label?.textContent ?? '').trim().replace(/\s+/g, ' ');
    }
}
